if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (!(await this.validateQuantityBeforeAddToCart(formData))) {
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async getCartJson() {
        // Shopify AJAX cart endpoint
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Failed to fetch cart');
        return res.json();
      }

      getExistingVariantQtyInCart(cart, variantId) {
        const idNum = parseInt(variantId, 10);
        if (!Number.isFinite(idNum)) return 0;

        const item = (cart.items || []).find((i) => i.variant_id === idNum);
        return item ? parseInt(item.quantity, 10) || 0 : 0;
      }

      async validateQuantityBeforeAddToCart(formData) {
        // Use the helper method to find the quantity input (may be outside the form)
        const qtyInput = this.getQuantityInput();
        
        // Get quantity to add - check formData first, then input value, default to 1
        const formQty = formData.get('quantity');
        const inputQty = qtyInput ? qtyInput.value : '1';
        const qtyToAdd = parseInt(formQty || inputQty, 10) || 1;
        
        const variantId = formData.get('id');

        // Get constraints from input or data attributes
        const min = qtyInput ? parseInt(qtyInput.getAttribute('min') || qtyInput.dataset.min || '1', 10) : 1;
        const step = qtyInput ? parseInt(qtyInput.getAttribute('step') || '1', 10) : 1;
        
        // Check for max in multiple places: max attribute, data-max, or data-max-per-customer
        let max = null;
        if (qtyInput) {
          const maxAttr = qtyInput.getAttribute('max');
          const dataMax = qtyInput.dataset.max;
          const dataMaxPerCustomer = qtyInput.dataset.maxPerCustomer;
          max = maxAttr ? parseInt(maxAttr, 10) : 
                dataMax ? parseInt(dataMax, 10) : 
                dataMaxPerCustomer ? parseInt(dataMaxPerCustomer, 10) : null;
        }
        
        // Also check on the form itself for a max-per-customer setting
        if (max === null && this.form.dataset.maxPerCustomer) {
          max = parseInt(this.form.dataset.maxPerCustomer, 10);
        }

        let message = '';

        if (!Number.isFinite(qtyToAdd) || qtyToAdd <= 0) {
          message = 'Please enter a valid quantity.';
        } else if (qtyToAdd < min) {
          message = `Minimum quantity is ${min}.`;
        } else if (step > 1 && (qtyToAdd - min) % step !== 0) {
          // Fixed: step validation should be relative to min, not zero
          message = `Quantity must be in increments of ${step} (starting from ${min}).`;
        }

        // If we have a max rule, enforce it against existing cart qty
        if (!message && max !== null && Number.isFinite(max) && max > 0) {
          try {
            const cart = await this.getCartJson();
            const existingQty = this.getExistingVariantQtyInCart(cart, variantId);
            const finalQty = existingQty + qtyToAdd;

            if (finalQty > max) {
              message = `Invalid quantity. Maximum allowed quantity (${max})`;
            }
          } catch (e) {
            // If cart fetch fails, allow add to proceed (fail open)
            console.warn('Could not validate cart quantity:', e);
          }
        }

        if (message) {
          // if (qtyInput) {
          //   qtyInput.setCustomValidity(message);
          //   qtyInput.reportValidity();
          // }
          this.handleErrorMessage(message);
          return false;
        }

        if (qtyInput) {
          qtyInput.setCustomValidity('');
          // normalize submitted quantity
          qtyInput.value = String(qtyToAdd);
        }
        formData.set('quantity', String(qtyToAdd));
        return true;
      }

      /**
       * Find the quantity input associated with this product form.
       * The quantity input is often OUTSIDE the form in Shopify themes,
       * so we need to search in multiple places.
       */
      getQuantityInput() {
        // 1. First check inside the form (for quick add modals, etc.)
        let qtyInput = this.form.querySelector('[name="quantity"]');
        if (qtyInput) return qtyInput;

        // 2. Try using the section ID from the product-form data attribute
        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          // Look for quantity input by ID pattern: Quantity-{section_id}
          qtyInput = document.getElementById(`Quantity-${sectionId}`);
          if (qtyInput) return qtyInput;
        }

        // 3. Look in the closest product info container
        const productInfo = this.closest('product-info') || 
                           this.closest('.product__info-container') ||
                           this.closest('.product__info-wrapper') ||
                           this.closest('[data-product-info]');
        if (productInfo) {
          qtyInput = productInfo.querySelector('.quantity__input') ||
                     productInfo.querySelector('[name="quantity"]') ||
                     productInfo.querySelector('input[type="number"].quantity__input');
          if (qtyInput) return qtyInput;
        }

        // 4. Look for quantity-input custom element nearby
        const quantityInputElement = this.closest('section')?.querySelector('quantity-input input') ||
                                     document.querySelector('quantity-input input.quantity__input');
        if (quantityInputElement) return quantityInputElement;

        // 5. Final fallback: look in the entire document section
        const section = this.closest('section');
        if (section) {
          qtyInput = section.querySelector('.quantity__input') ||
                     section.querySelector('[name="quantity"]');
          if (qtyInput) return qtyInput;
        }

        return null;
      }
    }
  );
}
